import { useState, useContext, useRef, useEffect, useCallback } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Coins, RotateCw, History, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { celebrateWin, soundEffects } from "@/lib/celebrations";

const SEGMENTS = [
  { label: '0x', color: '#FF3B30', multiplier: 0 },
  { label: '1.5x', color: '#007AFF', multiplier: 1.5 },
  { label: '2x', color: '#00E701', multiplier: 2 },
  { label: '0.5x', color: '#FF6B00', multiplier: 0.5 },
  { label: '3x', color: '#FFD600', multiplier: 3 },
  { label: '1x', color: '#A1A1AA', multiplier: 1 },
  { label: '5x', color: '#00F0FF', multiplier: 5 },
  { label: '0x', color: '#FF3B30', multiplier: 0 },
  { label: '1.2x', color: '#007AFF', multiplier: 1.2 },
  { label: '2x', color: '#00E701', multiplier: 2 },
  { label: '0.5x', color: '#FF6B00', multiplier: 0.5 },
  { label: '10x', color: '#FF007A', multiplier: 10 },
];

export const WheelPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [betAmount, setBetAmount] = useState(100);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const canvasRef = useRef(null);

  useEffect(() => {
    soundEffects.setEnabled(soundEnabled);
  }, [soundEnabled]);

  const drawWheel = useCallback((rot = 0) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2 - 20;
    
    // Clear
    ctx.fillStyle = '#0F0F10';
    ctx.fillRect(0, 0, width, height);
    
    // Save context
    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(rot * Math.PI / 180);
    
    // Draw segments
    const segmentAngle = (2 * Math.PI) / SEGMENTS.length;
    
    SEGMENTS.forEach((segment, i) => {
      const startAngle = i * segmentAngle;
      const endAngle = (i + 1) * segmentAngle;
      
      // Segment
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, radius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = segment.color;
      ctx.fill();
      ctx.strokeStyle = '#1A1B1F';
      ctx.lineWidth = 3;
      ctx.stroke();
      
      // Text
      ctx.save();
      ctx.rotate(startAngle + segmentAngle / 2);
      ctx.textAlign = 'right';
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 16px Rajdhani';
      ctx.fillText(segment.label, radius - 20, 6);
      ctx.restore();
    });
    
    // Center circle
    ctx.beginPath();
    ctx.arc(0, 0, 30, 0, Math.PI * 2);
    ctx.fillStyle = '#1A1B1F';
    ctx.fill();
    ctx.strokeStyle = '#FFD600';
    ctx.lineWidth = 3;
    ctx.stroke();
    
    ctx.restore();
    
    // Draw pointer
    ctx.beginPath();
    ctx.moveTo(centerX + radius + 10, centerY);
    ctx.lineTo(centerX + radius - 20, centerY - 15);
    ctx.lineTo(centerX + radius - 20, centerY + 15);
    ctx.closePath();
    ctx.fillStyle = '#FFD600';
    ctx.fill();
    
  }, []);

  useEffect(() => {
    drawWheel(rotation);
  }, [drawWheel, rotation]);

  const spinWheel = async () => {
    if (betAmount <= 0 || betAmount > (user?.balance || 0) || isSpinning) {
      toast.error("Invalid bet amount");
      return;
    }

    setIsSpinning(true);
    setLastResult(null);
    soundEffects.playSpin();

    try {
      // Call the proper wheel endpoint
      const response = await axios.post(`${API}/games/wheel/play`, {
        game_type: "wheel",
        bet_amount: betAmount,
        bet_data: {}
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });

      const resultIndex = response.data.segment;
      const segment = SEGMENTS[resultIndex];
      const winnings = response.data.winnings;
      
      // Calculate final rotation (at least 5 full spins + land on segment)
      const segmentAngle = 360 / SEGMENTS.length;
      const targetAngle = 360 - (resultIndex * segmentAngle) - segmentAngle / 2;
      const spins = 5 + Math.random() * 3;
      const finalRotation = rotation + (spins * 360) + targetAngle;
      
      // Animate spin
      const startRotation = rotation;
      const duration = 5000;
      const startTime = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function (ease out cubic)
        const eased = 1 - Math.pow(1 - progress, 3);
        
        const currentRotation = startRotation + (finalRotation - startRotation) * eased;
        setRotation(currentRotation);
        drawWheel(currentRotation);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Spin complete
          setLastResult({ segment, winnings, won: response.data.won });
          setHistory(prev => [{ mult: response.data.multiplier, label: segment.label, winnings, won: response.data.won }, ...prev.slice(0, 9)]);
          
          if (response.data.won) {
            toast.success(`${segment.label}! Won ₹${winnings.toFixed(2)}`);
            celebrateWin(response.data.multiplier, winnings);
          } else if (response.data.multiplier > 0) {
            toast.error(`${segment.label} - Got back ₹${winnings.toFixed(2)}`);
            soundEffects.playLoss();
          } else {
            toast.error(`0x - Lost ₹${betAmount}`);
            soundEffects.playLoss();
          }
          
          refreshBalance();
          setIsSpinning(false);
        }
      };
      
      requestAnimationFrame(animate);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to spin");
      setIsSpinning(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#FFD600]/20 flex items-center justify-center">
              <Coins className="w-6 h-6 text-[#FFD600]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Rajdhani']" data-testid="wheel-title">Wheel of Fortune</h1>
              <p className="text-[#A1A1AA] text-sm">Spin to win up to 10x!</p>
            </div>
          </div>
          <button 
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="p-2 rounded-lg bg-white/5 text-white/60 hover:text-white"
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Game Area */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-[#1A1B1F] border border-white/[0.08] overflow-hidden">
              <div className="p-6 flex justify-center">
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={400}
                  className="max-w-full"
                  data-testid="wheel-canvas"
                />
              </div>
              
              {/* Result Display */}
              {lastResult && (
                <div className={`p-4 text-center ${lastResult.won ? 'bg-[#00E701]/20' : 'bg-[#FF3B30]/20'}`}>
                  <span className={`text-3xl font-bold font-mono ${lastResult.won ? 'text-[#00E701]' : 'text-[#FF3B30]'}`}>
                    {lastResult.segment.label} 
                  </span>
                  <span className={`text-xl ml-3 ${lastResult.won ? 'text-[#00E701]' : 'text-[#FF3B30]'}`}>
                    {lastResult.won ? '+' : ''}₹{lastResult.winnings.toFixed(2)}
                  </span>
                </div>
              )}
              
              {/* Controls */}
              <div className="p-6 border-t border-white/[0.08]">
                <div className="flex gap-4 mb-4">
                  <div className="flex-1">
                    <label className="text-[#A1A1AA] text-sm mb-2 block">Bet Amount (₹)</label>
                    <Input
                      type="number"
                      value={betAmount}
                      onChange={(e) => setBetAmount(Number(e.target.value))}
                      disabled={isSpinning}
                      className="h-12 bg-black/20 border-white/10 text-white font-mono"
                      data-testid="wheel-bet-input"
                    />
                  </div>
                  <div className="flex gap-2 items-end">
                    {[50, 100, 500, 1000].map(amount => (
                      <Button
                        key={amount}
                        variant="outline"
                        size="sm"
                        onClick={() => setBetAmount(amount)}
                        disabled={isSpinning}
                        className="border-white/10 text-white/60 hover:text-white"
                      >
                        {amount}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <Button
                  onClick={spinWheel}
                  disabled={isSpinning || betAmount <= 0 || betAmount > (user?.balance || 0)}
                  className="w-full h-14 bg-[#FFD600] hover:bg-[#E6C200] text-black font-bold text-lg"
                  data-testid="wheel-spin-btn"
                >
                  <RotateCw className={`w-5 h-5 mr-2 ${isSpinning ? 'animate-spin' : ''}`} />
                  {isSpinning ? 'SPINNING...' : 'SPIN WHEEL'}
                </Button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Multipliers */}
            <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <h3 className="text-lg font-bold text-white font-['Rajdhani'] mb-4">Prizes</h3>
              <div className="grid grid-cols-3 gap-2">
                {[...new Set(SEGMENTS.map(s => s.label))].map((label, i) => {
                  const segment = SEGMENTS.find(s => s.label === label);
                  return (
                    <div 
                      key={i}
                      className="p-2 rounded text-center text-sm font-mono font-bold"
                      style={{ backgroundColor: `${segment.color}30`, color: segment.color }}
                    >
                      {label}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* History */}
            <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <div className="flex items-center gap-2 mb-4">
                <History className="w-5 h-5 text-[#A1A1AA]" />
                <h3 className="text-lg font-bold text-white font-['Rajdhani']">History</h3>
              </div>
              <div className="space-y-2">
                {history.length > 0 ? history.map((h, idx) => (
                  <div key={idx} className={`flex justify-between p-2 rounded-lg ${h.won ? 'bg-[#00E701]/10' : 'bg-[#FF3B30]/10'}`}>
                    <span className={`font-mono font-bold ${h.won ? 'text-[#00E701]' : 'text-[#FF3B30]'}`}>
                      {h.label}
                    </span>
                    <span className={`font-mono ${h.won ? 'text-[#00E701]' : 'text-[#FF3B30]'}`}>
                      {h.won ? '+' : ''}₹{h.winnings.toFixed(0)}
                    </span>
                  </div>
                )) : (
                  <p className="text-[#52525B] text-sm text-center">No spins yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
